// @givmo/board — the backfill session: reading it, and reading what came in under it.
//
// ══ FOUR MODEL FACTS THAT DECIDE HOW THIS SURFACE MUST BEHAVE ══════════════════════════════════
//
//   * A SESSION IS A PROVENANCE BOUNDARY, NOT A CONTAINER OR A WORKFLOW. Two states, one transition,
//     and closing is terminal: the row freezes and nothing further enters it. Its label and the
//     organization's own account of where the corpus came from are the ONLY place that origin is written
//     down — the module observed no part of a pre-module minute book. So the UI never offers to
//     re-open one, and it never implies a session is a folder records can be moved between.
//
//   * THERE IS ONE IMPORT DOOR, BATCHED OR NOT. `POST …/imported-records` simply takes an optional
//     `import_batch_id`. This feature adds a session and its reads; it never adds a second way for
//     something to become the organization's record. That is why the entry form here posts to the same
//     route the governing-document import does.
//
//   * A REPLAY IS "ALREADY IN THE BOOK", NOT AN ERROR. Inside a session the import is idempotent,
//     de-duplicated by CONTENT ADDRESS, and a retry answers 200 with `already_recorded: true` and
//     the record that was already entered. The reachable case is a bulk importer over a flaky
//     connection re-sending what it already sent — so rendering it as a failure would send an
//     officer hunting a problem that does not exist. {@link alreadyRecorded} is what the surface
//     branches on; the HTTP status carries the same signal.
//
//   * TWO CLOCKS, ALWAYS BOTH. `served_from`/`served_to` are the ORGANIZATION's account of a term;
//     `recorded_at`/`recorded_end_at` are the MODULE's account of when it was told. They never merge
//     — an organization that did not record an end date has not recorded one, and substituting the day we
//     were told would put a date in their mouth undetectably. Both are rendered on every roster
//     projection, which is what makes a 2026 entry of a 2019 term legible as exactly that.
//
// The session LIST is not confidentiality-scoped and the COUNT inside each session is. That asymmetry
// is deliberate on the backend's side — a session carries a label and a description of a box of paper,
// never record content, while an unscoped count would disclose withheld records by arithmetic — and it
// means the count this surface renders describes what THIS reader may see. It is never compared
// against anything, and no surface reports a difference.

import { extractList } from '@givmo/api-client/list';
import type { OrgBoardImportBatchOut, OrgBoardRecordOut } from '@givmo/api-client';
import type { BadgeProps } from '@givmo/design-system';
import { humanize } from '../../lib/display';

export { displayDate, displayInstant, todayAsDateInput } from '../../lib/display';

type BadgeTone = NonNullable<BadgeProps['tone']>;

/** One entered record as the review listing projects it — deliberately narrower than a record read. */
export interface ImportedRecordSummary {
  id: string;
  record_kind?: string | null;
  title?: string | null;
  occurred_on?: string | null;
  content_hash?: string | null;
  imported_source_note?: string | null;
  created_at?: string | null;
}

// ── The sessions ─────────────────────────────────────────────────────────────────────────────────

/** The organization's backfill sessions. */
export function extractImportBatches(body: unknown): OrgBoardImportBatchOut[] {
  return extractList<OrgBoardImportBatchOut>(body, 'data');
}

/** What came in under one session, in its short form. */
export function extractBatchRecords(body: unknown): ImportedRecordSummary[] {
  return extractList<ImportedRecordSummary>(body, 'data');
}

/**
 * Whether this session still accepts records.
 *
 * POSITIVE (`=== 'open'`), so a status this build does not recognize reads as CLOSED and the entry
 * form is withdrawn rather than offered against a session the backend will refuse. The permissive
 * direction here costs an officer a typed record and a refusal; the conservative one costs a click.
 */
export function isOpenBatch(batch: OrgBoardImportBatchOut): boolean {
  return (batch.status ?? '').toLowerCase() === 'open';
}

/** How a session's state reads. */
export function batchStatusLabel(status: string | null | undefined): string {
  switch ((status ?? '').toLowerCase()) {
    case 'open':
      return 'Open';
    case 'closed':
      return 'Closed';
    default:
      return humanize(status);
  }
}

/** Badge tone for a session's state. PURELY VISUAL — the words come from {@link batchStatusLabel}. */
export function batchStatusTone(status: string | null | undefined): BadgeTone {
  return (status ?? '').toLowerCase() === 'open' ? 'info' : 'neutral';
}

/**
 * The sessions in a stable reading order — newest first, by when they were opened.
 *
 * An organization works in the session it just opened, so it belongs at the top; a closed one is history and
 * sinks. Sessions with no `created_at` sort last rather than being dropped.
 */
export function sortBatches(batches: readonly OrgBoardImportBatchOut[]): OrgBoardImportBatchOut[] {
  return [...batches].sort((left, right) => instant(right.created_at) - instant(left.created_at));
}

/**
 * Who closed the session, as the words after "Closed <when>": the person's name, resolved by the
 * backend (CBG-UX2). Never the id — a name that did not resolve reads "by someone whose name isn't
 * shown", the record history's own words — and empty when the session names no closer.
 */
export function batchCloserSuffix(batch: OrgBoardImportBatchOut): string {
  if (!batch.closed_by_user_id) return '';
  const name = (batch.closed_by_display_name ?? '').trim();
  return name.length > 0 ? ` by ${name}` : ' by someone whose name isn’t shown';
}

/** How many records this reader can see in the session. Never compared against anything. */
export function recordCount(batch: OrgBoardImportBatchOut): number {
  return batch.record_count ?? 0;
}

// ── The import itself ────────────────────────────────────────────────────────────────────────────

/**
 * Whether the backend answered with a record it had ALREADY entered under this session.
 *
 * `already_recorded` is a fact about a REQUEST, so it is present only on the import response and null
 * on every read — which is exactly why this is read off the mutation's result and never off a record
 * fetched later.
 */
export function alreadyRecorded(record: OrgBoardRecordOut): boolean {
  return record.already_recorded === true;
}

/**
 * What to tell an officer after an import lands, in words that never read as a failure when none
 * occurred.
 *
 * ⚠ Two records that de-duplicate are byte-identical in EVERY field the content hash covers, so they
 * would read identically in the export — nothing is lost. An organization that genuinely means to record two
 * documents alike in title, body, date and provenance note distinguishes them in the provenance note,
 * which IS hashed ("minute book, page 14" / "page 15"). The message says so, because an officer who
 * meant to enter two things needs to know why they got one.
 */
export function importOutcomeLine(record: OrgBoardRecordOut): string {
  return alreadyRecorded(record)
    ? 'This one is already in the book — it was entered under this session before, so nothing was ' +
        'added twice. If you meant to record a second, different document, say which is which in ' +
        'where-it-came-from and record it again.'
    : 'Recorded in your minute book.';
}

/**
 * The record kinds a backfill enters: every kind the import door takes. Not a disclosure (CBG-UX11):
 * the door refuses that kind, because a disclosure's one door is the conflicts register.
 */
export const IMPORTABLE_RECORD_KINDS: readonly { value: string; label: string }[] = [
  { value: 'minutes', label: 'Minutes' },
  { value: 'resolution', label: 'Resolution' },
  { value: 'written_consent', label: 'Written consent' },
  // Approved (CBG-UX-COPY3's I3): what a secretary is holding, so "Bylaws" is findable first.
  { value: 'governing_document', label: 'Bylaws, a policy or another governing document' },
  { value: 'meeting', label: 'A meeting' },
  { value: 'acknowledgment', label: 'Acknowledgment' },
];

function instant(iso: string | null | undefined): number {
  if (!iso) return 0;
  const value = new Date(iso).getTime();
  return Number.isNaN(value) ? 0 : value;
}
